import { useEffect, useMemo } from 'react';
import { AccountId, PermissionId, ProjectId } from 'api/models';
import { Action, ObjectType, ProjectChannel } from 'api/pushes';
import {
  API_HOST,
  PUSHER_CLIENT_KEY,
  PUSHER_CLUSTER,
} from 'config/environment';
import { useAccount } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';
import { useProjectsFromInitData } from 'queries/account';
import { ApiKey } from 'services';
import { name } from './channels';

let worker;

if (typeof window.SharedWorker !== 'undefined') {
  worker = new SharedWorker(
    new URL('./worker/worker-pusher', import.meta.url),
    {
      type: 'module',
      name: 'pusher-worker',
    },
  );
}

worker?.port.start();

const createType = [Action.Create, ObjectType.Task];
const updateType = [Action.Update, ObjectType.Task];
const deleteType = [Action.Delete, ObjectType.Task];

export const useSharedWorkerMultiProjectWsManager = (
  onMessage: (message: any) => void,
) => {
  const { accountId } = useAccount();
  const { data: projectsData, isLoading } = useProjectsFromInitData();

  const projectIds = useMemo(
    () =>
      !isLoading && projectsData?.projects != null
        ? (projectsData?.projects ?? []).map((p) => p.projectId)
        : [],
    [isLoading, projectsData?.projects],
  );
  const userInfo = useUser();

  const pairs = useMemo(
    () =>
      projectIds
        .map(
          (projectId) =>
            [
              projectId,
              userInfo.permissions.projects[projectId]?.permissionSet
                ?.permissionSetId,
            ] as [number, number | undefined],
        )
        .filter(([, permissionId]) => permissionId != null)
        .map(([k, v]) => [k, v!]),
    [projectIds, userInfo.permissions.projects],
  );

  useEffect(() => {
    const portOnMessage = (e: MessageEvent<any>): void => {
      if (e.data.payload.objectType === 'TASK') {
        onMessage(e.data.payload);
      }
    };

    worker?.port.addEventListener('message', portOnMessage);

    return () => {
      worker?.port.removeEventListener('message', portOnMessage);
    };
  }, [onMessage]);

  useEffect(() => {
    const channelInfo = pairs.map(([projectId, permissionId]) => {
      return ProjectChannel(
        AccountId(accountId),
        PermissionId(permissionId),
        ProjectId(projectId),
      );
    });

    [createType, updateType, deleteType].forEach((type) => {
      channelInfo.forEach((channel) => {
        worker?.port.postMessage({
          action: 'SUBSCRIBE',
          payload: {
            event: type,
            channel,
            PUSHER_CLIENT_KEY,
            PUSHER_CLUSTER,
            channelName: name(channel),
            apiKey: ApiKey.header(),
            domainUrl: API_HOST,
          },
        });
      });
    });
    worker?.port.postMessage({
      action: 'CONNECT',
    });
    const onUnload = () => {
      worker?.port.postMessage({
        action: 'UNSUBSCRIBE',
      });
    };

    window.addEventListener('unload', onUnload);

    return () => {
      onUnload();
      window.removeEventListener('unload', onUnload);
    };
  }, [accountId, pairs]);
};
